import { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Link, useNavigate, useParams } from 'react-router-dom';
import './MeetingRounds.css';

function ParentMeetingBooking() {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');

  const [inviteData, setInviteData] = useState(null);
  const [slots, setSlots] = useState([]);
  const [existingBooking, setExistingBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [bookingSlotId, setBookingSlotId] = useState('');

  const formatDateTime = (value) => {
    if (!value) return '-';

    try {
      return new Intl.DateTimeFormat('nl-NL', {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(new Date(value));
    } catch {
      return value;
    }
  };

  const now = new Date();

const canRebook =
  !!existingBooking &&
  !!inviteData?.booking_close_at &&
  !!existingBooking?.start_at &&
  new Date(inviteData.booking_close_at) >= now &&
  new Date(existingBooking.start_at) > now;

const bookingClosed =
  !!inviteData?.booking_close_at &&
  new Date(inviteData.booking_close_at) < now;

const bookingNotOpenYet =
  !!inviteData?.booking_open_at &&
  new Date(inviteData.booking_open_at) > now;

  useEffect(() => {
    const fetchInviteSlots = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/meeting-round/invite/${inviteId}/slots`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const data = res.data?.data || null;
        setInviteData(data?.invite || null);
        setExistingBooking(data?.existing_booking || null);
        setSlots(Array.isArray(data?.slots) ? data.slots : []);
      } catch (err) {
        console.error('Error fetching invite slots', err);
        setInviteData(null);
        setExistingBooking(null);
        setSlots([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInviteSlots();
  }, [inviteId, token]);

  const handleChooseSlot = async (slotId) => {
    if (!slotId) return;

    const selectedSlot = slots.find((slot) => slot.slot_id === slotId);

    const confirmResult = await Swal.fire({
      title: 'Weet je het zeker?',
      html: canRebook
        ? `Wil je je afspraak wijzigen naar <strong>${formatDateTime(selectedSlot?.start_at)}</strong>?`
        : `Wil je dit oudergesprek inplannen op <strong>${formatDateTime(selectedSlot?.start_at)}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: canRebook ? 'Ja, wijzigen' : 'Ja, inschrijven',
      cancelButtonText: 'Annuleren',
      reverseButtons: true,
      focusCancel: true
    });

    if (!confirmResult.isConfirmed) return;

    setBookingSlotId(slotId);

    try {
      const endpoint = canRebook
        ? `${process.env.REACT_APP_BACKEND_URL}/meeting-round/invite/${inviteId}/rebook`
        : `${process.env.REACT_APP_BACKEND_URL}/meeting-round/invite/${inviteId}/book`;

      await axios.post(
        endpoint,
        { slot_id: slotId },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      await Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: canRebook
          ? 'Oudergesprek gewijzigd'
          : 'Oudergesprek ingepland',
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true
      });

      navigate('/');
    } catch (err) {
      console.error('Error saving meeting slot', err);

      await Swal.fire({
        icon: 'error',
        title: canRebook ? 'Wijzigen mislukt' : 'Inschrijven mislukt',
        text: err?.response?.data?.error || 'Opslaan mislukt.'
      });
    } finally {
      setBookingSlotId('');
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white p-4">
        <p>Loading...</p>
      </div>
    );
  }

  if (!inviteData) {
    return (
      <div className="bg-white p-4">
        <h2 className="text-center">Uitnodiging niet gevonden</h2>
        <Link to="/" className="btn btn-secondary w-100 rounded-0">
          Terug
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white p-4">
        <h2 className="text-center">Oudergesprek inplannen</h2>
        <hr className="seperator" />

        <div className="alert alert-info">
          <strong>{inviteData.round_title}</strong>
          <br />
          Leerling: {inviteData.student_name}
        </div>

        <div className="mb-3">
          {inviteData.round_description ? (
            <p>{inviteData.round_description}</p>
          ) : (
            <p className="text-muted">
              Er is geen extra informatie beschikbaar over dit oudergesprek.
            </p>
          )}
        </div>

        {existingBooking ? (
          <div className="alert alert-success">
            Je bent al ingeschreven voor dit oudergesprek op{' '}
            <strong>{formatDateTime(existingBooking.start_at)}</strong>.
            {canRebook && (
              <>
                <br />
                Je kunt je afspraak nog wijzigen zolang de inschrijfperiode open is.
              </>
            )}
          </div>
        ) : null}

        {!existingBooking && bookingNotOpenYet && (
          <div className="alert alert-warning">
            Inschrijven is mogelijk vanaf{' '}
            <strong>{formatDateTime(inviteData.booking_open_at)}</strong>.
          </div>
        )}

        {!existingBooking && bookingClosed && (
          <div className="alert alert-danger">
            De inschrijfperiode is gesloten.
          </div>
        )}

        {existingBooking && !canRebook && (
          <div className="alert alert-secondary">
            Wijzigen is niet meer mogelijk. De inschrijfperiode is gesloten of de afspraak ligt niet meer in de toekomst.
          </div>
        )}

        {!existingBooking && !bookingNotOpenYet && !bookingClosed && slots.length === 0 ? (
          <div className="alert alert-warning">
            Er zijn op dit moment geen beschikbare slots.
          </div>
        ) : null}

        {((!existingBooking && !bookingNotOpenYet && !bookingClosed) || canRebook) && (
          <>
            <p className="mb-3">
              {canRebook
                ? 'Kies een nieuw tijdslot om je afspraak te wijzigen:'
                : 'Kies een beschikbaar tijdslot:'}
            </p>

            <div className="meeting-home-list">
              {slots.map((slot) => (
                <div key={slot.slot_id} className="meeting-home-item">
                  <div className="meeting-home-item-main">
                    <p className="meeting-home-item-text mb-1">
                      <strong>{formatDateTime(slot.start_at)}</strong>
                    </p>
                    <p className="meeting-home-item-text mb-0">
                      Tot {formatDateTime(slot.end_at)}
                    </p>
                    {slot.note && (
                      <p className="meeting-home-item-text mb-0 text-muted">
                        {slot.note}
                      </p>
                    )}
                  </div>

                  <div className="meeting-home-item-action">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={bookingSlotId === slot.slot_id}
                      onClick={() => handleChooseSlot(slot.slot_id)}
                    >
                      {bookingSlotId === slot.slot_id
                        ? 'Bezig...'
                        : canRebook
                        ? 'Wijzig naar dit tijdslot'
                        : 'Kies dit tijdslot'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="mt-4">
          <Link to="/" className="btn btn-secondary w-100 rounded-0">
            Terug
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ParentMeetingBooking;